import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "redux/reducers/rootReducer";
import { getDomainCountryCode } from "redux/selectors/config";
import currencyFormatter from "utils/currencyFormatter";

import { getIsDispensaryDualLicense } from "./cartDispensary";
import { getUserId } from "./user";

export const getOrder = (state: RootState) => state.order;

export const getOrderData = (state: RootState) => state.order.data;

export const getOrderCartData = createSelector(
  [getOrderData],
  (orderData) => orderData?.cart
);

export const getOrderId = createSelector(
  [getOrderData],
  (orderData) => orderData?.id
);

export const getOrderGuestToken = createSelector(
  [getOrderData],
  (orderData) => orderData?.guestToken
);

export const getOrderExists = createSelector(
  [getOrderData],
  (orderData) => orderData && Object.entries(orderData).length
);

export const getOrderErrors = createSelector(
  [getOrderData],
  (orderData) => orderData?.errors
);

export const getOrderLoading = createSelector(
  [getOrder],
  (order) => order.loading.getReservation
);

export const getOrderError = createSelector(
  [getOrder],
  (order) => order.errors.getReservation
);

export const getSaveOrderLoading = createSelector(
  [getOrder],
  (order) => order.loading.saveReservation
);

export const getSaveOrderError = createSelector(
  [getOrder],
  (order) => order.errors.saveReservation
);

export const getOrderTaxType = createSelector(
  [getOrderData],
  (orderData) => orderData?.taxType
);

export const getOrderCartItems = createSelector(
  [getOrderCartData],
  (cartData) => cartData?.cartItems
);

export const getHasMedicalItems = createSelector(
  [getOrderCartItems],
  (cartItems) => !!cartItems?.some((item) => item.medical)
);

export const getOrderCartLabel = createSelector(
  [getIsDispensaryDualLicense, getHasMedicalItems],
  (isDualLicense, hasMedicalItems) => {
    if (!isDualLicense) {
      return null;
    }
    return hasMedicalItems ? "MEDICAL" : "RECREATIONAL";
  }
);

export const getOrderFulfillmentMechanism = createSelector(
  [getOrderData],
  (order) =>
    order?.fulfillmentMechanism === "delivery" ? "Delivery" : "Pickup"
);

export const getShowGuestSignup = createSelector(
  [getUserId, getOrderData],
  (userId, orderData) =>
    !userId && !orderData?.emailAddressAssociatedWithExistingUserAccount
);

export const getCanceledStatusChange = createSelector([getOrderData], (order) =>
  order?.statusChanges.find((statusChange) => statusChange.to === "canceled")
);

export const getOrderTotalWithTax = createSelector(
  [getOrderData],
  (order) => (order?.total ?? 0) + (order?.tax ?? 0)
);

export const getDeliveryFee = createSelector(
  [getOrderData],
  (orderData) => orderData?.deliveryFee
);

export const getDeliveryFeeFormatted = createSelector(
  [getDeliveryFee, getDomainCountryCode],
  (fee, country) => {
    return fee ? currencyFormatter(fee, country) : "Free!";
  }
);
